import { useEffect, useState } from 'react';
import { Alert, FormHelperText, Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { DocumentAttachments } from '../../components/document-attachments';
import { toDateInputValue } from '../../../../utils/date';
import { ILookupResponse } from '../../../../model/apm/base/lookup-response';
import { IPortalDocumentResponse } from '../../../../model/apm/document/portal-document-response';
import { IPublicAccountAddEditRequest } from '../../../../model/apm/public-account/request/public-account-add-edit-request';
import { IPublicAccountResponse } from '../../../../model/apm/public-account/response/public-account-response';
import { IPublicAccountCategoryResponse } from '../../../../model/apm/public-account/response/public-account-category-response';
import { categoryIdOfSubcategory, subcategoryOptions } from './public-account-category-utils';

interface PublicAccountDrawerProps {
    open: boolean;
    initial?: IPublicAccountResponse;
    files: IPortalDocumentResponse[];
    categories: IPublicAccountCategoryResponse[];
    documentTypes: ILookupResponse[];
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IPublicAccountAddEditRequest) => void;
    onUploadFile: (documentTypeId: number, file: File, onProgress: (percent: number) => void) => Promise<void>;
    onDeleteFile: (file: IPortalDocumentResponse) => void;
}

type FormState = IPublicAccountAddEditRequest & { categoryId?: number };

export function PublicAccountDrawer({
    open, initial, files, categories, documentTypes, isSaving, onClose, onSave, onUploadFile, onDeleteFile,
}: PublicAccountDrawerProps) {
    const [form, setForm] = useState<FormState>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    // Publicações geradas pelo Diário Oficial são somente consulta (a API rejeita alteração e exclusão)
    const readOnly = !!initial?.isFromOfficialDiary;
    const errors = {
        subcategoryId: showValidationErrors && !form.subcategoryId,
        publishDate: showValidationErrors && !form.publishDate,
        description: showValidationErrors && !form.description?.trim(),
    };

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? {
                categoryId: initial.categoryId || categoryIdOfSubcategory(categories, initial.subcategoryId),
                subcategoryId: initial.subcategoryId,
                publishDate: toDateInputValue(initial.date),
                description: initial.description,
            }
            : {});
        setShowValidationErrors(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initial]);

    function update(patch: Partial<FormState>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (!form.subcategoryId || !form.publishDate || !form.description?.trim()) {
            setShowValidationErrors(true);
            return;
        }
        onSave({ subcategoryId: form.subcategoryId, publishDate: form.publishDate, description: form.description.trim() });
    }

    const options = subcategoryOptions(categories, form.categoryId);
    // Registros antigos podem apontar para uma subcategoria fora da lista; mantém a opção para não perder o vínculo
    const subcategories = initial && form.subcategoryId === initial.subcategoryId && !options.some((s) => s.id === initial.subcategoryId)
        ? [...options, { id: initial.subcategoryId, categoryId: initial.categoryId, description: initial.subcategory, label: initial.subcategory || `Código ${initial.subcategoryId}` }]
        : options;
    const title = readOnly ? 'Publicação do Diário Oficial' : isEdit ? 'Editar publicação' : 'Nova publicação';
    const subtitle = isEdit
        ? [initial?.category, initial?.subcategory].filter(Boolean).join(' › ')
        : 'Salve a publicação para anexar os documentos';

    return (
        <FormDrawer
            open={open}
            icon={<AccountBalanceIcon />}
            title={title}
            subtitle={subtitle}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar publicação'}
            isSaving={isSaving}
            saveDisabled={readOnly}
            onClose={onClose}
            onSave={handleSave}
        >
            {readOnly && (
                <Alert severity="info">
                    Esta publicação foi gerada pelo Diário Oficial e não pode ser alterada nem excluída por aqui. Os dados e anexos estão disponíveis apenas para consulta.
                </Alert>
            )}

            <FormDrawerGroup title="Classificação" description="Onde a publicação aparece na seção Contas Públicas do portal.">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel>Categoria</InputLabel>
                        <Select
                            fullWidth
                            disabled={readOnly}
                            value={form.categoryId ?? ''}
                            onChange={(e) => update({ categoryId: e.target.value === '' ? undefined : Number(e.target.value), subcategoryId: undefined })}
                        >
                            {categories.map((c) => <MenuItem key={c.id} value={c.id}>{c.description}</MenuItem>)}
                        </Select>
                        <FormHelperText>Filtra as subcategorias</FormHelperText>
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Subcategoria</InputLabel>
                        <Select
                            fullWidth
                            disabled={readOnly}
                            value={subcategories.some((s) => s.id === form.subcategoryId) ? form.subcategoryId : ''}
                            onChange={(e) => {
                                const subcategoryId = e.target.value === '' ? undefined : Number(e.target.value);
                                update({ subcategoryId, categoryId: form.categoryId ?? categoryIdOfSubcategory(categories, subcategoryId) });
                            }}
                            error={errors.subcategoryId}
                        >
                            {subcategories.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                        </Select>
                        {errors.subcategoryId
                            ? <FormHelperText error>Subcategoria é obrigatória</FormHelperText>
                            : categories.length === 0 && <FormHelperText>Nenhuma categoria de contas públicas disponível</FormHelperText>}
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Publicação">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Data de publicação</InputLabel>
                        <OutlinedInput
                            fullWidth
                            type="date"
                            disabled={readOnly}
                            value={form.publishDate || ''}
                            onChange={(e) => update({ publishDate: e.target.value || undefined })}
                            error={errors.publishDate}
                        />
                        {errors.publishDate && <FormHelperText error>Data de publicação é obrigatória</FormHelperText>}
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel required>Descrição</InputLabel>
                        <OutlinedInput
                            fullWidth
                            multiline
                            minRows={3}
                            autoFocus={!isEdit}
                            disabled={readOnly}
                            placeholder="Ex.: Relatório Resumido da Execução Orçamentária - 1º bimestre"
                            value={form.description || ''}
                            onChange={(e) => update({ description: e.target.value })}
                            error={errors.description}
                        />
                        {errors.description && <FormHelperText error>Descrição é obrigatória</FormHelperText>}
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup
                title="Documentos"
                description={readOnly ? 'Arquivos publicados pelo Diário Oficial.' : 'Arquivos publicados no portal. Os anexos são salvos na hora.'}
            >
                <DocumentAttachments
                    id="public-account-file"
                    files={files}
                    documentTypes={documentTypes}
                    readOnly={readOnly}
                    disabledMessage={isEdit ? undefined : 'Salve a publicação para anexar documentos. O formulário continua aberto após salvar.'}
                    onUpload={onUploadFile}
                    onDelete={onDeleteFile}
                />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
